import { ICustomComponent, IStockLimits } from '@/context/ProductDataStore'
import { IProductImage } from '@/types'

type UnknownRecord = Record<string, any>

const getPartProduct = (part: UnknownRecord): UnknownRecord => {
	if (part?.product && typeof part.product === 'object') {
		return part.product as UnknownRecord
	}
	return part
}

export const getPartMaterialInformation = (part: UnknownRecord): Array<{
	materialId: string
	kibu_name: string
	finish?: string
	hex_code?: string
}> => {
	const product = getPartProduct(part)
	if (Array.isArray(product?.materialInformation)) {
		return product.materialInformation
	}
	if (Array.isArray(part?.materialInformation)) {
		return part.materialInformation
	}
	return []
}

export const getPartAvailableOptions = (part: UnknownRecord): UnknownRecord[] => {
	const product = getPartProduct(part)
	if (Array.isArray(product?.availableOptions)) {
		return product.availableOptions
	}
	return []
}

export const getPartId = (part: UnknownRecord): string => {
	const product = getPartProduct(part)
	return (
		product?.productItemId ||
		product?._id ||
		product?.defaultProductItemId ||
		part?.id ||
		part?.productId ||
		part?.product_item_id ||
		''
	)
}

export const getPartName = (part: UnknownRecord): string => {
	const product = getPartProduct(part)
	return (
		product?.productTitle ||
		product?.productName ||
		part?.partName ||
		part?.name ||
		'Unnamed part'
	)
}

export const getPartSubtitle = (part: UnknownRecord): string | null => {
	const product = getPartProduct(part)
	return product?.subtitle || product?.componentTypeName || product?.optionTitle || null
}

export const getPartPrice = (part: UnknownRecord): number => {
	const product = getPartProduct(part)
	return (
		product?.price?.unit_amount ??
		product?.price ??
		product?.unitPrice ??
		part?.unit_amount ??
		0
	)
}

export const getPartImage = (part: UnknownRecord): string | null => {
	const heroImage = getPartHeroImage(part)
	if (heroImage) {
		return heroImage.url || heroImage.thumbnailUrl || null
	}

	const product = getPartProduct(part)
	const images = product?.images || part?.images
	if (Array.isArray(images) && images.length) {
		const first = images[0]
		if (typeof first === 'string') {
			return first
		}
		if (first && typeof first === 'object') {
			return first.url || first.imageUrl || first.primary || first.src || null
		}
	}
	if (typeof part?.imageUrl === 'string') {
		return part.imageUrl
	}
	if (typeof part?.image === 'string') {
		return part.image
	}
	if (typeof product?.imageUrl === 'string') {
		return product.imageUrl
	}
	return null
}

export const getPartHeroImage = (part: UnknownRecord): IProductImage | null => {
	const product = getPartProduct(part)
	const heroImage = product?.heroImage || product?.hero_image || part?.heroImage || part?.hero_image
	if (!heroImage || typeof heroImage !== 'object') {
		return null
	}

	const record = heroImage as UnknownRecord
	const foreground =
		record.foregroundUrl ||
		record.foreground_url ||
		record.url ||
		record.imageUrl ||
		record.primary ||
		record.src
	if (typeof foreground !== 'string') {
		return null
	}

	const background = record.backgroundUrl || record.background_url
	const thumbnail =
		record.thumbnailUrl ||
		record.thumbnail_url ||
		record.thumbnail ||
		record.thumb ||
		foreground
	const alt = typeof record.alt === 'string' ? record.alt : getPartName(part)

	return {
		url: foreground,
		backgroundUrl: typeof background === 'string' ? background : undefined,
		thumbnailUrl: typeof thumbnail === 'string' ? thumbnail : undefined,
		showThumbnail: true,
		alt,
	}
}

export const getPartImages = (part: UnknownRecord): IProductImage[] => {
	const product = getPartProduct(part)
	const images = product?.images || part?.images
	const fallbackAlt = getPartName(part)
	const parsed: Array<{
		url: string
		thumbnailUrl?: string
		alt: string
		order?: number
	}> = []

	if (Array.isArray(images)) {
		images.forEach((image) => {
			if (typeof image === 'string') {
				parsed.push({ url: image, alt: fallbackAlt })
				return
			}

			if (image && typeof image === 'object') {
				const record = image as UnknownRecord
				const url = record.url || record.imageUrl || record.primary || record.src
				if (typeof url !== 'string') {
					return
				}

				const thumbnail =
					record.thumbnailUrl ||
					record.thumbnail_url ||
					record.thumbnail ||
					record.thumb
				const alt = typeof record.alt === 'string' ? record.alt : fallbackAlt
				const order = typeof record.order === 'number' ? record.order : undefined

				parsed.push({
					url,
					thumbnailUrl: typeof thumbnail === 'string' ? thumbnail : undefined,
					alt,
					order,
				})
			}
		})
	}

	if (!parsed.length) {
		const fallbackUrl = getPartImage(part)
		if (fallbackUrl) {
			return [{
				url: fallbackUrl,
				showThumbnail: true,
				alt: fallbackAlt,
			}]
		}
		return []
	}

	return parsed
		.slice()
		.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
		.map((image) => ({
			url: image.url,
			thumbnailUrl: image.thumbnailUrl,
			showThumbnail: true,
			alt: image.alt || fallbackAlt,
		}))
}

export const getPartStockLimits = (part: UnknownRecord): IStockLimits | undefined => {
	const product = getPartProduct(part)
	const stock = product?.calculatedStock || product?.stockLimits || product?.stock || null
	if (!stock || typeof stock !== 'object') {
		return undefined
	}

	return {
		stockStatus: stock.stockStatus || stock.status,
		stockMessage: stock.stockMessage || stock.message || null,
		isPreorder: stock.isPreorder ?? null,
		outOfStockSince: stock.outOfStockSince ?? null,
		availableForSale: stock.availableForSale ?? stock.isAvailableForSale ?? null,
		remainingStock: stock.remainingStock ?? stock.availableStock ?? null,
		maxDelayDays: stock.maxDelayDays ?? null,
		estimatedDispatchDays: stock.estimatedDispatchDays ?? null,
	}
}

export const getPartCatalogueId = (part: UnknownRecord): string | null => {
	const product = getPartProduct(part)
	return product?.productCatalogueId || product?.catalogueId || null
}

export const getPartComponentTypeId = (part: UnknownRecord): string | null => {
	const product = getPartProduct(part)
	if (typeof product?.componentTypeId === 'string') {
		return product.componentTypeId
	}
	if (typeof product?.componentType === 'string') {
		return product.componentType
	}
	if (Array.isArray(product?.components) && typeof product.components[0]?.componentType === 'string') {
		return product.components[0].componentType
	}
	return null
}

export const buildPartComponents = (
	part: UnknownRecord,
	selectedMaterialId?: string | null
): ICustomComponent[] => {
	const product = getPartProduct(part)
	const components = Array.isArray(product?.components) ? product.components : []
	const mapped: ICustomComponent[] = []

	components.forEach((component: UnknownRecord) => {
		const baseComponent = {
			componentVariantId: { $oid: component.defaultVariant || component.componentVariantId },
			componentType: component.componentType,
			name: component.name,
			display: component.display,
			materialOptions: component.materialOptions,
			defaultMaterial: component.defaultMaterial,
			chosenMaterial: {
				$oid: selectedMaterialId || component.defaultMaterial || ''
			},
		} as ICustomComponent

		const quantity = typeof component.quantity === 'number' && component.quantity > 0 ? component.quantity : 1
		for (let i = 0; i < quantity; i += 1) {
			mapped.push({
				...baseComponent,
				position: component.position ?? null
			})
		}
	})

	return mapped
}

/**
 * Whether a colour is dark enough that black line art on it is hard to see, so the
 * outline should flip to white. Uses WCAG relative luminance: dark when white text
 * would contrast more than black.
 */
export const isDarkColour = (hex: string | null | undefined): boolean => {
	const raw = (hex || '').trim().replace(/^#/, '')
	const full = raw.length === 3 ? raw.split('').map(char => char + char).join('') : raw
	if (!/^[0-9a-f]{6}$/i.test(full)) {
		return false
	}
	const channel = (index: number) => {
		const value = Number.parseInt(full.slice(index, index + 2), 16) / 255
		return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
	}
	const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4)
	// Contrast with white (1.05 / (L + 0.05)) beats contrast with black ((L + 0.05) / 0.05) below ~0.179.
	return luminance < 0.179
}

/** Light neutral the rendered/uploaded part images sit on (the drawings sit on the colour itself). */
export const REPAIR_RENDER_BACKGROUND = '#f1eee9'

/**
 * What the repair store shows for a part, from the backend's `repairImages`:
 * - render: a Blender render per colour (only once every colour the part is sold in has one)
 * - image: an uploaded image (parts that aren't rendered, e.g. electronics)
 * Returns null when the part should keep its drawing on a colour swatch.
 */
export const getPartRepairImage = (
	part: UnknownRecord,
	materialId?: string | null
): { source: 'render' | 'image'; url: string } | null => {
	const product = part?.product && typeof part.product === 'object' ? part.product : part
	const info = (product?.repairImages || part?.repairImages) as UnknownRecord | undefined
	if (!info || typeof info !== 'object') return null
	if (info.source === 'render' && info.renders && typeof info.renders === 'object') {
		const renders = info.renders as Record<string, string>
		const url = (materialId && renders[materialId]) || renders.default || Object.values(renders)[0]
		return url ? { source: 'render', url } : null
	}
	if (info.source === 'image' && typeof info.imageUrl === 'string' && info.imageUrl) {
		return { source: 'image', url: info.imageUrl }
	}
	return null
}

/** Every render URL for a part, to preload so switching colours is instant. */
export const getPartRenderUrls = (part: UnknownRecord): string[] => {
	const product = part?.product && typeof part.product === 'object' ? part.product : part
	const info = (product?.repairImages || part?.repairImages) as UnknownRecord | undefined
	return info?.source === 'render' && info.renders ? Object.values(info.renders as Record<string, string>) : []
}
